import type { ConfiguratorProgress, VehicleRef } from '../domain';

export const SAVED_CONFIG_SCHEMA_VERSION = 1;

export type ServicesSelection = {
	assicurazioneRC: string | number | null;
	assicurazioneKasko: string | number | null;
	furtoIncendio: string | number | null;
	vetturaSostitutiva: string | number | null;
	gomme: {
		value: string | number;
		unlimited: boolean;
		quantity: number;
		estive: number;
		invernali: number;
	} | null;
	tutelaLegale: boolean;
	manutenzione: boolean;
};

export type LeasingFormSnapshot = {
	rentalDuration: number;
	totalKm: number;
	advancePayment: number;
	commissionValue: number;
	commissionType: 'EURO' | 'PERCENTAGE';
};

/**
 * L'offerta Quicko su cui è stato stampato il PDF.
 */
export type SelectedOffer = {
	raw: Record<string, unknown>;
	noleggiatore: string;
	canone: number;
	durata: number;
	kmAnnui: number;
	anticipo: number;
	clientName: string;
	clientType: string;
};

/**
 * Quanto serve a ricostruire la configurazione.
 */
export type SavedConfigurationPayload = {
	progress: ConfiguratorProgress;
	leasing: LeasingFormSnapshot;
	services: ServicesSelection;
	offer: SelectedOffer | null;
	fuel: string;
	currency: string;
	parentId: string | null;
};

/** Una pronta consegna non ha configurazione: si riseleziona per id. */
export type StockPayload = Omit<SavedConfigurationPayload, 'progress'>;

/** Etichette e codici della riga di lista: tutto da `metadata`. */
export type ConfigurationListing = {
	makeCode: string;
	makeLabel: string;
	modelCode: string;
	modelLabel: string;
	/** eurotaxCode dell'allestimento, o vehicleCode per la pronta consegna. */
	trimCode: string | null;
	trimLabel: string | null;
};

/** Traccia storica, mai riusata al caricamento. */
export type ConfigurationPricesAtSave = {
	currency: string;
	listPriceNoVat: number;
	optionalsNoVat: number;
	totalNoVat: number;
};

export type UnavailableReason =
	| 'payload_invalid'
	| 'trim_delisted'
	| 'listing_unavailable'
	| 'options_invalid'
	| 'stock_sold'
	| 'excluded'
	| 'not_quotable';

export type SavedConfigurationRow = {
	id: string;
	version: number;
	code: string;
	createdAt: string;
	updatedAt: string;
	available: boolean;
	unavailableReason: UnavailableReason | null;
	vehicleType: 'new' | 'stock';
	vehicleStockId: string | null;
	vehicleRef: VehicleRef | null;
	imageUrl: string | null;
	listing: ConfigurationListing;
	pricesAtSave: ConfigurationPricesAtSave;
};

type PayloadVariant =
	| {
			vehicleType: 'new';
			vehicleStockId: null;
			payload: SavedConfigurationPayload;
	  }
	| { vehicleType: 'stock'; vehicleStockId: string; payload: StockPayload };

export type SavedCarConfiguration = SavedConfigurationRow & PayloadVariant;

export type ConfigurationDraft = {
	listing: ConfigurationListing;
	pricesAtSave: ConfigurationPricesAtSave;
	vehicleRef: VehicleRef | null;
	imageUrl: string | null;
	parentId: string | null;
} & PayloadVariant;

export type NewConfigurationDraft = Extract<
	ConfigurationDraft,
	{ vehicleType: 'new' }
>;
export type StockConfigurationDraft = Extract<
	ConfigurationDraft,
	{ vehicleType: 'stock' }
>;
export type NewCarConfiguration = Extract<
	SavedCarConfiguration,
	{ vehicleType: 'new' }
>;

export type GetCarConfigurationsRequest = {
	search?: string;
	page?: number;
	pageSize?: number;
};

export type SavedConfigurationsPage = {
	items: SavedConfigurationRow[];
	total: number;
};
